// imagepasteReject — imagepaste 拒否 reason の日本語化 + 残り時間整形ヘルパ。
//
// ## なぜ存在するか
// imagepaste の拒否 (rate-limited / サイズ超過 / 非対応形式 / 権限なし /
// アップロード失敗 / ID 無効) は **チャット欄ではなくキャンバス中央のトースト**に
// 出す (= 貼付時はユーザがキャンバスを注視しており、チャットの system 行は気付き
// にくい、2026-06-14)。以前は `RoomSession.ts` の reducer が `view.system` に push
// していたが廃止し、`useImagepaste` がトースト state を立てて `CanvasStage` の
// `ImagepasteToastOverlay` が描画する。
//
// reason→日本語マップはここに集約 (= 旧 RoomSession の reasonText を移設)。トースト
// と、クライアント事前バリデーション (start() 内) の両方が同じ文言を使う。
//
// rate-limited のときだけ server が `retryAfterMs` を載せ、client が毎秒
// カウントダウンする (例「画像貼り付けは あと N 秒 待ってね」)。秒整形は
// `uiFeedback.ts` の `formatMs` と同方針 (ceiling 秒、分繰り上げ) だが、ここでは
// 「残り N 秒」の文脈に合わせた専用文言を組む。

import type { WhiteboardImagepasteRejected } from '@oekaki/protocol';

/** server が返す拒否 reason の union (protocol schema 由来)。 */
export type ImagepasteRejectReason = WhiteboardImagepasteRejected['reason'];

/**
 * reason → 静的な日本語文言。rate-limited は server の `retryAfterMs` を使って
 * カウントダウンを別途組むため、ここでは fallback (= retryAfterMs 不在時) の
 * 文言だけを持つ。
 *
 * 旧 `RoomSession.ts:545-552` の reasonText と同一文言 (= 回帰なし)。
 */
const REASON_TEXT: Record<ImagepasteRejectReason, string> = {
  'no-permission': '画像貼り付けの権限がありません',
  'content-too-large': '画像サイズが大きすぎます (最大 5MB)',
  'invalid-content-type': '対応していない画像形式です (PNG / JPEG / GIF)',
  'rate-limited': '画像貼り付けの間隔が短すぎます (30秒お待ちください)',
  'upload-failed': '画像のアップロードに失敗しました',
  'invalid-upload': '画像のアップロード ID が無効です',
};

/**
 * reason (+ 任意の server message) を日本語トースト文言へ。未知 reason は
 * 「画像貼り付けが拒否されました (reason)」へフォールバック (= 旧 RoomSession と同じ)。
 */
export const imagepasteRejectText = (reason: string, message?: string | undefined): string => {
  const known = REASON_TEXT[reason as ImagepasteRejectReason];
  if (known !== undefined) return known;
  return `画像貼り付けが拒否されました (${reason})${message ? `: ${message}` : ''}`;
};

/**
 * rate-limited の残り ms → 「あと N 秒」風の文言。`uiFeedback.formatMs` と同じ
 * ceiling 秒・分繰り上げの作法 (= 1 秒余分に待つほうが、早すぎる再試行より親切)。
 * 60 秒以上は「あと M分S秒」。0 以下は呼び出し側がトースト自体を消すので、ここでは
 * 防御的に「もう貼り付けできます」を返す。
 */
export const imagepasteRetryText = (remainingMs: number): string => {
  if (remainingMs <= 0) return '画像貼り付けは もう大丈夫だよ';
  const sec = Math.max(1, Math.ceil(remainingMs / 1_000));
  if (sec >= 60) {
    const mins = Math.floor(sec / 60);
    const remSec = sec % 60;
    const dur = remSec > 0 ? `${mins}分${remSec}秒` : `${mins}分`;
    return `画像貼り付けは あと${dur} 待ってね`;
  }
  return `画像貼り付けは あと${sec}秒 待ってね`;
};
